/**
 * GTM Event Types
 * All event interfaces include a typed "event" property
 */

// EEC Item Interface (Enhanced Ecommerce)
export interface GTMEECItem {
  item_name: string
  item_id: string
  item_brand: string
  item_category: string
  currency: string
  discount: string
  index: string
  item_list_id: string
  item_list_name: string
  price: string
  flow_name: string
  retroactive_cover?: string
  deductible?: string
  policy_duration?: string
  policy_limit?: string
  erp?: string
}

// Purchase Start Event
export interface GTMPurchaseStartEvent {
  event: 'purchase_start'
  flow_name: 'mmp'
  dob_month?: string
  dob_year?: string
  user_id?: string
}

// MM Policyholder Details Event (PRD)
export interface GTMPolicyholderDetailsEvent {
  event: 'mm_policyholder_details'
  flow_name: 'mmp'
  identity_number: string
  dob_month: string
  dob_year: number
}

// Add Profession Data Event
export interface GTMAddProfessionDataEvent {
  event: 'add_profession_data'
  step_name: string
  experience: string
  region: string
  place: string
  speciality: string
  surgeon: string
  policyEffectiveDate: string
}

// Add Declarations Details Data Event
export interface GTMAddDeclarationsDetailsDataEvent {
  event: 'add_declarations_details_data'
  flow_name: 'mmp'
  step_name: string
  [key: string]: string
}

// Terms and Conditions Event
export interface GTMTermsAndConditionsEvent {
  event: 'terms_and_conditions'
  flow_name: 'mmp'
  step_name: string
  quote: string
}

// Update Quota Detail Event
export interface GTMUpdateQuotaDetailEvent {
  event: 'update_quota_detail'
  flow_name: 'mmp'
  step_name: string
  quote: string
  selected_field: string
  selected_value: string
}

// Quotes Received — per-quotation item shape
export interface GTMQuotationItem {
  insurer_name: string
  policy_id: string
  price: number
  discount_price?: number
  available_plan_names: string
  split_option_available?: boolean
  discount_applied?: boolean
  discount_name?: string
  discount_amount?: number
  total_discount_amount?: number
  policy_duration: number
  policy_limit: number
  deductible: number
  retroactive_cover: string
  erp: string
}

// Quotes Received Event
export interface GTMQuotesReceivedEvent {
  event: 'quotes_received'
  flow_name: 'mmp'
  step_name: string
  quotation_id: string
  no_of_quotes: number
  specialty?: string
  lowest_price: number
  lowest_discounted_price?: number
  highest_price: number
  highest_discounted_price?: number
  all_insurer_names: string[]
  quotations: GTMQuotationItem[]
}

// Quote Viewed Event
export interface GTMQuoteViewedEvent {
  event: 'mm_quote_viewed'
  flow_name: 'mmp'
  insurer_name: string
  quotation_id: string
  policy_number: string
  policy_duration: string
  policy_limit: string
  deductible: string
  price: number
  retroactive_cover: string
  erp: string
  no_of_benefit?: number
  discount_yearly?: { year: number; discount: number }[]
  benefits?: { title: string; link: string }[]
}

// Benefit Link Clicked Event
export interface GTMBenefitLinkClickedEvent {
  event: 'mm_benefit_link_clicked'
  flow_name: 'mmp'
  benefit_title: string
  insurer_name: string
  quotation_id: string
  policy_number: string
  benefit_link: string
}

// Select Item Event (Ecommerce)
export interface GTMSelectItemEvent {
  event: 'select_item'
  ecommerce: {
    item_list_name: string
    item_list_id: string
    items: GTMEECItem
  }
}

// Pay Clicked Event
export interface GTMPayClickedEvent {
  event: 'mm_pay_clicked'
  flow_name: 'mmp'
  qualification: string
  place_of_work: string
  quotation_id: string
  insurer_name: string
  policy_number: string
  policy_duration: string
  policy_limit: string
  deductible: string
  policy_price_amount: number
  extended_reporting_period: number
  retroactive_cover: number
  subtotal: number
  vat: number
  total_amount: number
  schs_number: string
  schs_expiry_date: string
  place_of_practice: string
}

// Begin Checkout Event (Ecommerce)
export interface GTMBeginCheckoutEvent {
  event: 'begin_checkout'
  flow_name: 'mmp'
  // PRD MM Policy Payment Started flat attributes
  coupon_code_applied: boolean
  coupon_code: string
  coupon_code_amount: number
  quotation_id: string
  insurance_company: string
  policy_number: string
  invoice_value: string
  vat_percentage: number
  vat_amount: number
  total_amount: number
  payment_method: string
  ecommerce: {
    currency: string
    value: string
    items: GTMEECItem
  }
}

// Purchase Complete Event
export interface GTMPurchaseCompleteEvent {
  event: 'purchase'
  flow_name: 'mmp'
  // PRD MM Policy Payment Success flat attributes
  quotation_id: string
  insurer_name: string
  policy_number: string
  policy_duration: string
  policy_price_amount: number
  subtotal: number
  vat: number
  total_amount: number
  payment_method: string
  ecommerce: {
    currency: string
    value: string
    coupon: string
    transaction_id: string
    tax: string
    items: GTMEECItem
  }
}

// Payment Failed Event
export interface GTMPaymentFailedEvent {
  event: 'mm_policy_payment_failed'
  flow_name: 'mmp'
  quotation_id: string
  insurer_name: string
  policy_number: string
  policy_duration: string
  policy_price_amount: number
  subtotal: number
  vat: number
  total_amount: number
  payment_method: string
  status: string
  payment_link: string
}

// Summary Category Info Event
export interface GTMSummaryCategoryInfoEvent {
  event: 'summary_category_info'
  flow_name: 'mmp'
  step_name: string
  category: string
}

// Error Captured Event
export interface GTMErrorCapturedEvent {
  event: 'error_captured'
  flow_name: 'mmp'
  step_name: string
  error_code: string
  error_message: string
  type: string
}

// Navigation Menu Event
export interface GTMNavigationMenuEvent {
  event: 'navigation_menu'
  flow_name: 'mmp'
  product: string
  selection: string
}

// Purchased Policy Option Event
export interface GTMPurchasedPolicyOptionEvent {
  event: 'purchased_policy_option'
  flow_name: 'mmp'
  selection: string
}

// MMP Policy Action Event
export interface GTMMMPPolicyActionEvent {
  event: 'mmp_policy_action'
  flow_name: 'mmp'
  policy_number: string
  insurer_name: string
  years_of_experience: number
  region_of_practice: string
  place_of_practice: string
  specialty: string
  action: string
}

// Policy Renewal Started Event
export interface GTMPolicyRenewalStartedEvent {
  event: 'policy_renewal_started'
  flow_name: 'mmp'
  policy_number: string
  previous_insurer: string
  coupon_code_applied?: boolean
  coupon_code?: string
  coupon_code_amount?: number
  quotation_id: string
  insurance_company: string
  invoice_value: string
  vat_percentage: number
  vat_amount: number
  total_amount: number
  payment_method?: string
}

// Policy Renewed Event
export interface GTMPolicyRenewedEvent {
  event: 'mm_policy_renewed'
  flow_name: 'mmp'
  qualification: string
  place_of_work: string
  quotation_id: string
  insurer_name: string
  policy_number: string
  policy_duration: string
  policy_limit: string
  deductible: string
  policy_price_amount: number
  extended_reporting_period: number
  retroactive_cover: number
  subtotal: number
  vat: number
  total_amount: number
  schs_number: string
  schs_expiry_date: string
  place_of_practice: string
  payment_method?: string
}

// Policy Renewal Failed Event
export interface GTMPolicyRenewalFailedEvent {
  event: 'mm_policy_renewal_failed'
  flow_name: 'mmp'
  quotation_id: string
  insurer_name: string
  policy_number: string
  policy_duration: string
  policy_price_amount: number
  subtotal: number
  vat: number
  total_amount: number
  payment_method: string
  status: string
  payment_link: string
}

// Login Event
export interface GTMLoginEvent {
  event: 'login'
  flow_name: 'mmp'
  user_id?: string
}

// Logout Event
export interface GTMLogoutEvent {
  event: 'logout'
  flow_name: 'mmp'
}

/**
 * Union type for all GTM events
 */
export type GTMEvent =
  | GTMPurchaseStartEvent
  | GTMPolicyholderDetailsEvent
  | GTMAddProfessionDataEvent
  | GTMAddDeclarationsDetailsDataEvent
  | GTMTermsAndConditionsEvent
  | GTMUpdateQuotaDetailEvent
  | GTMSelectItemEvent
  | GTMBeginCheckoutEvent
  | GTMSummaryCategoryInfoEvent
  | GTMErrorCapturedEvent
  | GTMPurchaseCompleteEvent
  | GTMQuotesReceivedEvent
  | GTMQuoteViewedEvent
  | GTMBenefitLinkClickedEvent
  | GTMPayClickedEvent
  | GTMPaymentFailedEvent
  | GTMNavigationMenuEvent
  | GTMPurchasedPolicyOptionEvent
  | GTMMMPPolicyActionEvent
  | GTMPolicyRenewalStartedEvent
  | GTMPolicyRenewedEvent
  | GTMPolicyRenewalFailedEvent
  | GTMLoginEvent
  | GTMLogoutEvent

/**
 * Flow name constant - MMP only
 */
export const GTM_FLOW_NAME = 'mmp' as const
